import React, { useEffect, useRef, useState, useMemo } from "react";
import ReactDOM from "react-dom";

export type LocFilter = "all" | "inside" | "outside";

type Props = {
  open: boolean;
  onClose: () => void;

  // initial values
  initialLocation: LocFilter;
  initialTypes: string[];
  initialStatuses: string[];

  // data derived from your projects.ts
  allTypes: string[];     // every unique type tag
  allStatuses: string[];  // every unique project.status

  onApply: (payload: {
    location: LocFilter;
    types: string[];
    statuses: string[];
  }) => void;
  onClear?: () => void;
};

const EXECUTED_WHITELIST = new Set<string>([
  "Residential building Build",
  "Interior Design build",
]); // keeps explicit ones if you want

const isExecutedType = (t: string) =>
  EXECUTED_WHITELIST.has(t) || /\bbuild\s*$/i.test(t.trim());

const QuickFilterDrawer: React.FC<Props> = ({
  open,
  onClose,
  initialLocation,
  initialTypes,
  initialStatuses,
  allTypes,
  allStatuses,
  onApply,
  onClear,
}) => {
  const [loc, setLoc] = useState<LocFilter>(initialLocation);
  const [types, setTypes] = useState<string[]>(initialTypes);
  const [statuses, setStatuses] = useState<string[]>(initialStatuses);
  const firstBtn = useRef<HTMLButtonElement | null>(null);

  // split Type chips -> Executed vs Designed
const { executedTypes, designedTypes } = useMemo(() => {
  const executed: string[] = [];
  const designed: string[] = [];
  allTypes.forEach((raw) => {
    const t = (raw ?? "").trim();
    if (!t) return;
    (isExecutedType(t) ? executed : designed).push(t);
  });
  // remove dupes & sort
  const uniq = (arr: string[]) => Array.from(new Set(arr)).sort((a, b) => a.localeCompare(b));
  return { executedTypes: uniq(executed), designedTypes: uniq(designed) };
}, [allTypes]);

  useEffect(() => {
    if (open) {
      setLoc(initialLocation);
      setTypes(initialTypes);
      setStatuses(initialStatuses);
      setTimeout(() => firstBtn.current?.focus(), 0);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, initialLocation, initialTypes, initialStatuses]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const toggle = (arr: string[], v: string) =>
    arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];

  const toggleType = (t: string) => setTypes((prev) => toggle(prev, t));
  const toggleStatus = (s: string) => setStatuses((prev) => toggle(prev, s));

  // ---------- inline styles (same look you already have) ----------
  const overlayStyle: React.CSSProperties = {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,.45)",
    opacity: open ? 1 : 0,
    visibility: open ? "visible" : "hidden",
    transition: "opacity .2s ease, visibility .2s ease",
    zIndex: 2147483646,
  };

  const drawerStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    height: "100vh",
    width: "min(480px,92vw)",
    background: "#fff",
    color: "#111",
    boxShadow: "8px 0 40px rgba(0,0,0,.35)",
    transform: open ? "translate3d(0,0,0)" : "translate3d(-102%,0,0)",
    transition: "transform .28s ease",
    display: "flex",
    flexDirection: "column",
    zIndex: 2147483647,
    borderRight: "1px solid rgba(0,0,0,.08)",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 18px",
    borderBottom: "1px solid rgba(0,0,0,.08)",
  };

  const contentStyle: React.CSSProperties = {
    padding: "14px 18px 6px",
    overflow: "auto",
    background: "#fff",
  };
  const groupStyle: React.CSSProperties = { marginBottom: 18 };
  const labelStyle: React.CSSProperties = {
    fontWeight: 700,
    marginBottom: 8,
    color: "#111",
  };

  const smallNote: React.CSSProperties = {
    color: "#666",
    fontWeight: 500,
    fontSize: ".9rem",
    marginLeft: 6,
  };

  const segWrap: React.CSSProperties = {
    display: "flex",
    gap: 6,
    padding: 4,
    border: "1px solid rgba(0,0,0,.12)",
    borderRadius: 999,
    background: "#f7f7f7",
    width: "fit-content",
  };
  const seg = (active: boolean): React.CSSProperties => ({
    appearance: "none",
    border: 0,
    background: active ? "#111" : "transparent",
    color: active ? "#fff" : "#111",
    cursor: "pointer",
    padding: "8px 12px",
    borderRadius: 999,
    fontWeight: 600,
    opacity: active ? 1 : 0.85,
  });

  const chipsWrap: React.CSSProperties = {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  };
  const chip = (on: boolean): React.CSSProperties => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    background: on ? "#111" : "#fff",
    border: `1px solid ${on ? "#111" : "rgba(0,0,0,.12)"}`,
    color: on ? "#fff" : "#111",
    cursor: "pointer",
    userSelect: "none",
  });

  const footer: React.CSSProperties = {
    marginTop: "auto",
    padding: "12px 18px",
    borderTop: "1px solid rgba(0,0,0,.08)",
    display: "flex",
    alignItems: "center",
    gap: 12,
    background: "#fff",
  };
  const btnPrimary: React.CSSProperties = {
    background: "#111",
    color: "#fff",
    border: 0,
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
  };
  const btnSecondary: React.CSSProperties = {
    background: "transparent",
    color: "#111",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: 10,
    padding: "10px 14px",
    cursor: "pointer",
  };

  return ReactDOM.createPortal(
    <>
      <div style={overlayStyle} onClick={onClose} aria-hidden={!open} />
      <aside style={drawerStyle} aria-hidden={!open} role="dialog">
        <header style={headerStyle}>
          <h2 style={{ margin: 0, fontSize: "1.1rem" }}>Filter Projects</h2>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              appearance: "none",
              border: 0,
              background: "transparent",
              fontSize: 28,
              lineHeight: 1,
              cursor: "pointer",
              opacity: 0.7,
              color: "#111",
            }}
          >
            ×
          </button>
        </header>

        <div style={contentStyle}>
          {/* Location */}
          <div style={groupStyle}>
            <div style={labelStyle}>Location</div>
            <div style={segWrap}>
              <button
                ref={firstBtn}
                style={seg(loc === "all")}
                onClick={() => setLoc("all")}
              >
                All
              </button>
              <button
                style={seg(loc === "inside")}
                onClick={() => setLoc("inside")}
              >
                Inside Lebanon
              </button>
              <button
                style={seg(loc === "outside")}
                onClick={() => setLoc("outside")}
              >
                Outside Lebanon
              </button>
            </div>
          </div>

          {/* Executed */}
          <div style={groupStyle}>
            <div style={labelStyle}>Executed</div>
            <div style={chipsWrap}>
              {executedTypes.map((t) => (
                <label key={t} style={chip(types.includes(t))}>
                  <input
                    type="checkbox"
                    checked={types.includes(t)}
                    onChange={() => toggleType(t)}
                    style={{ display: "none" }}
                  />
                  <span>{t}</span>
                </label>
              ))}
              {executedTypes.length === 0 && (
                <span style={{ color: "#666" }}>No executed types found.</span>
              )}
            </div>
          </div>

          {/* Designed */}
          <div style={groupStyle}>
            <div style={labelStyle}>
              Designed <span style={smallNote}>(everything else)</span>
            </div>
            <div style={chipsWrap}>
              {designedTypes.map((t) => (
                <label key={t} style={chip(types.includes(t))}>
                  <input
                    type="checkbox"
                    checked={types.includes(t)}
                    onChange={() => toggleType(t)}
                    style={{ display: "none" }}
                  />
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Status */}
          <div style={groupStyle}>
            <div style={labelStyle}>Status</div>
            <div style={chipsWrap}>
              {allStatuses.map((s) => (
                <label key={s} style={chip(statuses.includes(s))}>
                  <input
                    type="checkbox"
                    checked={statuses.includes(s)}
                    onChange={() => toggleStatus(s)}
                    style={{ display: "none" }}
                  />
                  <span>{s}</span>
                </label>
              ))}
              {allStatuses.length === 0 && (
                <span style={{ color: "#666" }}>No status values found.</span>
              )}
            </div>
          </div>
        </div>

        <footer style={footer}>
          <button
            style={btnSecondary}
            onClick={() => {
              setLoc("all");
              setTypes([]);
              setStatuses([]);
              onClear?.();
            }}
          >
            Clear all
          </button>
          <div style={{ flex: 1 }} />
          <button
            style={btnPrimary}
            onClick={() => onApply({ location: loc, types, statuses })}
          >
            Apply
          </button>
        </footer>
      </aside>
    </>,
    document.body
  );
};

export default QuickFilterDrawer;
